import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '../services/confirmation.service';
import { PlanService } from '../services/plan.service';
import { Router } from '@angular/router';
import { PermissionService } from '../../features/security/services/permission.service';

/**
 * Quota interceptor — intercepts HTTP 402 (PLAN_LIMIT_REACHED) responses
 * and displays a user-friendly confirmation dialog with upgrade option.
 */
export const quotaInterceptor: HttpInterceptorFn = (req, next) => {
    const translate = inject(TranslateService);
    const confirmationService = inject(ConfirmationService);
    const planService = inject(PlanService);
    const router = inject(Router);
    const permissionService = inject(PermissionService);

    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status === 402 && error.error?.code === 'PLAN_LIMIT_REACHED') {
                const details = error.error.details || {};
                const resource = details.resource as string || 'unknown';
                const current = details.current as number ?? 0;
                const max = details.max as number ?? 0;
                const planName = details.planName as string || '';

                const resourceLabel = translate.instant(planService.RESOURCE_LABELS[resource] || resource);
                const icon = planService.RESOURCE_ICONS[resource] || 'pi pi-exclamation-triangle';

                const title = translate.instant('QUOTA.LIMIT_REACHED_TITLE');
                const message = translate.instant('QUOTA.LIMIT_REACHED_MSG', {
                    resource: resourceLabel,
                    current,
                    max,
                    plan: planName,
                });

                const isSuperAdmin = permissionService.isSuperAdmin();

                confirmationService.confirm({
                    title,
                    message,
                    icon,
                    type: 'warning',
                    confirmText: isSuperAdmin
                        ? translate.instant('QUOTA.MANAGE_PLAN')
                        : translate.instant('QUOTA.CONTACT_ADMIN'),
                    cancelText: translate.instant('QUOTA.UNDERSTOOD'),
                }).then((result) => {
                    if (result && isSuperAdmin) {
                        router.navigate(['/plans']);
                    }
                    // Refresh limits cache after quota error
                    const companyId = localStorage.getItem('selectedCompanyId');
                    if (companyId) {
                        planService.clearCache(+companyId);
                        planService.getLimits(+companyId, true).subscribe();
                    }
                });
            }

            return throwError(() => error);
        })
    );
};
